import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { db } from "../firebase";

export type StaffMember = {
  id: string;
  name: string;
  role: string;
  bio: string;
  imageUrl?: string;
  email?: string;
  sortOrder?: number;
};

const fallbackStaff: StaffMember[] = [
    {
      id: "andy-adams",
      name: "Andy Adams",
      role: "Principal",
      bio: "Leads school administration, family partnership, and the daily mission of Zion Classical Academy."
    },
    {
      id: "staci-hueftle",
      name: "Staci Hueftle",
      role: "Pre-School Teacher",
      bio: "Welcomes young learners into joyful habits of attention, play, early literacy, and Christian character."
    },
    {
      id: "beth-koepke",
      name: "Beth Koepke",
      role: "Kindergarten Teacher",
      bio: "Guides kindergarten students through phonics, math, Scripture, music, art, and classroom readiness."
    },
    {
      id: "britney-krueger",
      name: "Britney Krueger",
      role: "1st Grade Teacher",
      bio: "Serves lower elementary students through grammar school foundations and classroom formation."
    },
    {
      id: "krista-novotne",
      name: "Krista Novotne",
      role: "2nd Grade Teacher",
      bio: "Teaches lower elementary students in a Christ-centered, classical grammar school environment."
    },
    {
      id: "julia-mcconaghay",
      name: "Julia McConaghay",
      role: "3rd Grade",
      bio: "Supports students as they grow in knowledge, responsibility, and joyful learning."
    },
    {
      id: "jamie-klein",
      name: "Jamie Klein",
      role: "4th and 5th Grade Teacher",
      bio: "Leads upper elementary students through a rich curriculum that includes logic, writing, history, literature, science, and faith formation."
    },
    {
      id: "brittany-bauer",
      name: "Brittany Bauer",
      role: "Office Manager",
      bio: "Supports families, records, communication, schedules, and the school office."
    },
    {
      id: "sharon-shriver",
      name: "Sharon Shriver",
      role: "Special Education, Title I, Art",
      bio: "Serves students through specialized support, Title I services, and art instruction."
    },
    {
      id: "gina-hoffman",
      name: "Gina Hoffman",
      role: "Paraeducator",
      bio: "Assists classrooms and students through practical instructional support."
    },
    {
      id: "katie-sutton",
      name: "Katie Sutton",
      role: "Food Service Chef",
      bio: "Serves students through the school lunch program and kitchen operations."
    },
    {
      id: "becky-hamik",
      name: "Becky Hamik",
      role: "Physical Education Professor",
      bio: "Leads students in physical education, movement, health, and teamwork."
    },
    {
      id: "lanette-grothen",
      name: "Lanette Grothen",
      role: "Paraeducator",
      bio: "Supports teachers and students through classroom assistance."
    },
    {
      id: "carla-post",
      name: "Carla Post",
      role: "Music Teacher",
      bio: "Teaches music as part of ZCA's classical Christian formation."
    }
  ];

function mapPublicStaffDoc(id: string, data: Record<string, unknown>): StaffMember {
  const firstName = String(data.firstName ?? "").trim();
  const lastName = String(data.lastName ?? "").trim();
  const displayName = String(data.name ?? data.displayName ?? "").trim() || `${firstName} ${lastName}`.trim();

  return {
    id,
    name: displayName || "ZCA Staff",
    role: String(data.role ?? data.title ?? data.position ?? "Staff"),
    bio: String(data.bio ?? data.summary ?? ""),
    imageUrl: String(data.imageUrl ?? data.photoUrl ?? ""),
    email: String(data.email ?? ""),
    sortOrder: Number(data.sortOrder ?? 999)
  };
}

export async function getStaff(options: { useFallback?: boolean } = {}): Promise<StaffMember[]> {
  if (!db) return options.useFallback === false ? [] : fallbackStaff;

  try {
    const snap = await getDocs(
      query(
        collection(db, "publicStaff"),
        orderBy("sortOrder", "asc"),
        limit(100)
      )
    );
    const items = snap.docs
      .filter((item) => (item.data() as any).isPublished === true)
      .map((item) => mapPublicStaffDoc(item.id, item.data()));
    return items.length ? items : options.useFallback === false ? [] : fallbackStaff;
  } catch (error) {
    console.warn("[public-web] Failed to load Firestore publicStaff", error);
    return options.useFallback === false ? [] : fallbackStaff;
  }
}

export function getFallbackStaff() {
  return [
    ...fallbackStaff
  ];
}
